import React from 'react'

export type Breadcrumb = {
    id: number,
    title: string,
    route?: string,
    options?: BreadcrumbOptions,
    isDefault?: boolean,
}

export type BreadcrumbOptions = {
    type?: string,
    campaignId?: string,
}

type BreadcrumbsFunctionContextType = {
    registerBreadcrumb: () => number,
    upsertBreadcrumb: (id: number, title: string, route?: string, options?: BreadcrumbOptions) => void,
    removeBreadcrumb: (id: number) => void,
}

export const BreadcrumbsContext = React.createContext<Array<Breadcrumb>>([])
export const BreadcrumbsFunctionsContext = React.createContext<BreadcrumbsFunctionContextType | undefined>(undefined) as React.Context<BreadcrumbsFunctionContextType>

export function BreadcrumbsProvider (props: { children?: React.ReactNode }) {
    const [breadcrumbs, setBreadcrumbs] = React.useState<Array<Breadcrumb>>([])
    const nextIdRef = React.useRef(0)

    const breadcrumbFunctions = React.useMemo<BreadcrumbsFunctionContextType>(() => ({
        registerBreadcrumb: () => {
            return nextIdRef.current++
        },
        upsertBreadcrumb: (id, title, route, options) => {
            setBreadcrumbs(bs => {
                const b = { id, title, route, options }

                for (const i in bs) {
                    const index = parseInt(i, 10)
                    if (bs[index].id < id) continue

                    if (bs[index].id === id) {
                        return [...bs.slice(0, index), b, ...bs.slice(index + 1)]
                    }

                    return [...bs.slice(0, index), b, ...bs.slice(index)]
                }

                return [...bs, b]
            })
        },
        removeBreadcrumb: ((id: number) => {
            setBreadcrumbs(bs => {
                return bs.filter(b => b.id !== id)
            })
        }),
    }), [])

    return (
        <BreadcrumbsFunctionsContext.Provider value={breadcrumbFunctions}>
            <BreadcrumbsContext.Provider value={breadcrumbs}>
                {props.children}
            </BreadcrumbsContext.Provider>
        </BreadcrumbsFunctionsContext.Provider>
    )
}
